import React, { Fragment, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Badge, DateDisplayer, Typography } from '@telavita-core/react-design-kit'
import { Trans } from '@lingui/macro'
import PropTypes from 'prop-types'
import moment from 'moment'


import { handleProfileCode } from 'utils'
import NotificationInline from '../NotificationInline'
import ToggleFloatButton from '../ToggleFloatButton'
import { Avatar } from 'components'

import * as actions from 'redux/actions'
import { modalTypes } from 'containers/ModalManager/ModalManager'
import { SCHEDULED, ATTENDING, CANCELED } from '../../settings/_scheduleStatusSettings'
import { DATE_DISPLAYER_STATUS } from 'settings/_dateDisplayerStatusSettings'
import { isScheduleNow } from 'helpers/scheduleHelpers'

import { ReactComponent as InfoIcon } from 'static/svg/icon-info.svg'
import { ReactComponent as ExclamationIcon } from 'static/svg/exclamation-icon.svg'

const EligibleToScheduleWarning = () => (
  <div className='ConsultationCard__FeedbackWarning'>
    <div>
      <ExclamationIcon />
    </div>
    <Typography variant='content3' color='danger'>
      Se até a data da próxima consulta seu nome ainda não constar na lista de beneficiários do convênio, 
      todos os próximos agendamentos serão cancelados.
    </Typography>
  </div>
)

function ConsultationCard({
  avatarSize,
  avatarUrl,
  id,
  planName,
  isTimeUp,
  isToday,
  menuList,
  meetingHash,
  name,
  price,
  sessoinId,
  hasPrice,
  status,
  startDate,
  endDate,
  professionalCode,
  professionalDocumentType,
  professionalDocument,
  isMedicalReturn = false,
  tokenHasBeenFilled,
  planGroupCode,
  planGroupName,
  eligibleToSchedule
}) {
  const [show, handleShow] = useState(false)
  const dispatch = useDispatch()

  useEffect(() => {
    let interval

    const checkScheduleDate = () => {
      let isNow = isScheduleNow(startDate, endDate)
      if(isNow){
        show === false && handleShow(true)
      }else{
        show === true && handleShow(false)
      }
    }

    if(status === SCHEDULED || status === ATTENDING){
      checkScheduleDate()
      interval = setInterval(checkScheduleDate, 15000)
    }
    
    return () => {
      clearInterval(interval)
    }

  }, [show, status, startDate, endDate])

  function openCanceledScheduleDetailModal() {
    dispatch(actions.showModal(modalTypes.CANCELED_DETAIL_MODAL, id))
  }

  return (
    <div className={`ConsultationCard${isToday ? '--cardColor' : ''}`}>
      <div style={{ display: 'flex', flexDirection: 'row', width: '100%', alignItems: 'center', gap: '15px' }}>
        <DateDisplayer
          date={new Date(startDate)}
          label={DATE_DISPLAYER_STATUS[status]?.label}
          variant={DATE_DISPLAYER_STATUS[status]?.variant}
          labelTextColor={DATE_DISPLAYER_STATUS[status]?.labelTextColor}
          hourTextColor={DATE_DISPLAYER_STATUS[status]?.hourTextColor}
          weekdayColor={DATE_DISPLAYER_STATUS[status]?.hourTextColor}
          icon={DATE_DISPLAYER_STATUS[status]?.icon}
          iconColor={DATE_DISPLAYER_STATUS[status]?.iconColor}
          customClassName='ConsultationCard__BoxDateDisplay'
        />

        <div className='ConsultationCard__Cardcenter'>
          <div className='Cardcenter__Psycologist'>
            <div className='avatar'>
              <Avatar
                imgUrl={avatarUrl}
                avatarSize={avatarSize}
              />
            </div>

            <div className='pinfo'>
              { sessoinId
                ? <Typography variant='content2'>{sessoinId}ª sessão com</Typography>
                :<Typography variant='content2'>Sessão com</Typography>
              }
              <Typography weight='bolder' variant='content2' customClassName="Cardcenter__Psycologist__Name">
                {name}
              </Typography>

              <div className="ConsultationCard__proDocument">
                <Typography variant='content3'>
                  {handleProfileCode(professionalCode)}
                  {professionalDocumentType}
                  {professionalDocument}
                </Typography>
              </div>

              <div>
                <div className='ConsultationCard__PlanName'>
                  <Typography variant='content3'>
                    ID:{id}
                  </Typography>
                  <Typography variant='content3'>
                    {planName}
                  </Typography>
                </div>
                <Typography variant='content3'>
                  {
                    hasPrice && (
                      <Fragment>
                        <Trans>Valor:</Trans> <span>{price}</span>
                      </Fragment>
                    )
                  }
                </Typography>
              </div>

              {isMedicalReturn && (
                <div className='ConsultationCard__BadgeWrapper'>
                  <Badge colorScheme='grey'>
                    <Typography variant='content3' weight='bold' inheritColor>
                      CONSULTA DE RETORNO  
                    </Typography>
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className='ConsultationCard__HideOnMobile'>
          {(!eligibleToSchedule && status === SCHEDULED) && (
            <EligibleToScheduleWarning />
          )}
        </div>

        {status === CANCELED
          ? <InfoIcon role="button" onClick={openCanceledScheduleDetailModal} />
          : null
        }

        {
          show ? (
            <div className='ConsultationCard__Notification'>
              <NotificationInline
                initialTime={moment().diff(startDate) < 0 ? moment().diff(startDate) * -1 : 0}
                virtualRoomHash={meetingHash}
                isTimeUp={isTimeUp}
                tokenHasBeenFilled={tokenHasBeenFilled}
                planGroupCode={planGroupCode}
                startDate={startDate}
                endDate={endDate}
                scheduleId={id}
                planGroupName={planGroupName}
              />
            </div>
          ) : status === SCHEDULED ? (
            <div className='ConsultationCard__Icon'>
              <ToggleFloatButton
                menuList={menuList}
              />
            </div>
          ) : (
            <div className='ConsultationCard__NoIcon'></div>
          ) 
        }
      </div>

      <div className='ConsultationCard__HideOnDesktop'>
        {(!eligibleToSchedule && status === SCHEDULED) && (
          <EligibleToScheduleWarning />
        )}
      </div>
    </div>
  )
}

ConsultationCard.propTypes = {
  hasPrice: PropTypes.bool,
  status: PropTypes.string,
  id: PropTypes.any,
  name: PropTypes.string,
  sessoinId: PropTypes.any,
  price: PropTypes.string,
  time: PropTypes.string,
  date: PropTypes.string,
  menuList: PropTypes.any,
  avatarUrl: PropTypes.string,
  avatarSize: PropTypes.string,
  iconName: PropTypes.any,
}

export {
  ConsultationCard
}
